

import apiService from './apiService';

export interface UazApiConfig {
  baseUrl: string;
  token: string;
  adminToken?: string;
}

export interface UazApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

export interface InstanceStatusResponse {
  instance: WhatsAppInstance;
  status: {
    connected: boolean;
    loggedIn: boolean;
    jid: {
      user: string;
      agent: number;
    } | null;
  };
}

export interface WhatsAppInstance {
  id: string;
  token: string;
  status: 'connected' | 'disconnected' | 'connecting' | 'qr';
  paircode: string;
  qrcode: string;
  name: string;
  profileName: string;
  profilePicUrl: string;
  isBusiness: boolean;
  plataform: string;
  systemName: string;
  owner: string;
  lastDisconnect: string;
  lastDisconnectReason: string;
  adminField01: string;
  adminField02: string;
  openai_apikey: string;
  chatbot_enabled: boolean;
  chatbot_ignoreGroups: boolean;
  chatbot_stopConversation: string;
  chatbot_stopMinutes: number;
  chatbot_stopWhenYouSendMsg: number;
  created: string;
  updated: string;
  currentTime: string;
  msg_delay_min: number;
  msg_delay_max: number;
}

export interface ConnectionStatus {
  connected: boolean;
  connecting: boolean;
  loggedIn?: boolean;
  jid?: {
    user: string;
    agent: number;
  } | null;
  error?: string;
  qrcode?: string;
  paircode?: string;
  instance?: WhatsAppInstance;
}

export interface SendMessageRequest {
  number: string;
  text: string;
  readchat?: boolean;
}

export interface SendMediaRequest {
  number: string;
  type: 'image' | 'video' | 'audio' | 'document';
  file: string;
  text?: string;
  readchat?: boolean;
}

/**
 * Serviço para integração com a API UazAPI (WhatsApp)
 * @author Matheus
 */
class UazApiService {
  private config: UazApiConfig;

  /**
   * Construtor do serviço UazAPI
   * @author Matheus
   * @param config Configuração da API
   */
  constructor(config: UazApiConfig) {
    this.config = config;
  }

  /**
   * Realiza requisição HTTP para a API UazAPI
   * @author Matheus
   * @param endpoint Endpoint da API
   * @param method Método HTTP
   * @param body Corpo da requisição
   * @param useAdminToken Se deve usar token administrativo
   * @param customToken Token customizado
   * @returns Promise com resposta da API
   */
  private async makeRequest<T>(
    endpoint: string,
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET',
    body?: any,
    useAdminToken: boolean = false,
    customToken?: string
  ): Promise<UazApiResponse<T>> {
    try {
      const url = `${this.config.baseUrl}${endpoint}`;
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'User-Agent': 'SDR-Odonto/1.0'
      };
      
      if (useAdminToken && this.config.adminToken) {
        headers['admintoken'] = this.config.adminToken;
      } else {
        headers['token'] = customToken || this.config.token;
      }



      const requestOptions: RequestInit = {
        method,
        headers,
      };

      if (body && (method === 'POST' || method === 'PUT')) {
        requestOptions.body = JSON.stringify(body);
        
      }

      const response = await fetch(url, requestOptions);

      if (!response.ok) {
        const errorText = await response.text();
        
        let errorMessage = 'Erro na requisição para UazAPI';
        
        switch (response.status) {
          case 401:
            errorMessage = 'Token inválido ou expirado. Verifique suas credenciais.';
            break;
          case 403:
            errorMessage = 'Acesso negado. Verifique as permissões do token.';
            break;
          case 404:
            errorMessage = 'Endpoint não encontrado. Verifique a documentação da API.';
            break;
          case 409:
            // Tratamento específico para conflito de conexão
            if (endpoint.includes('/instance/connect')) {
              errorMessage = 'Instância já está conectada. Use /instance/status para verificar o estado atual.';
            } else {
              errorMessage = 'Conflito de estado. Verifique se a operação já foi realizada.';
            }
            break;
          case 429:
            errorMessage = 'Limite de requisições excedido. Tente novamente em alguns minutos.';
            break;
          case 500:
            errorMessage = 'Erro interno do servidor UazAPI. Tente novamente mais tarde.';
            break;
          default:
            errorMessage = `Erro HTTP ${response.status}: ${errorText}`;
        }

        return {
          success: false,
          error: errorMessage,
          message: errorText
        };
      }

      const responseText = await response.text();
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        
        return {
          success: false,
          error: `Resposta da API não é um JSON válido para ${endpoint}. A API pode estar retornando dados corrompidos ou uma página de erro.`,
          message: responseText.substring(0, 200) + (responseText.length > 200 ? '...' : '')
        };
      }
      
      return {
        success: true,
        data,
        message: 'Requisição realizada com sucesso'
      };

    } catch (error) {
      let errorMessage = 'Erro de conectividade com a UazAPI';
      
      if (error instanceof TypeError && error.message.includes('fetch')) {
        errorMessage = 'Erro de CORS ou conectividade. Verifique se o proxy está configurado ou se a API permite requisições do navegador.';
        
      } else if (error instanceof Error) {
        errorMessage = `Erro de rede: ${error.message}`;
        
      }

      return {
        success: false,
        error: errorMessage
      };
    }
  }

  async testConnection(): Promise<UazApiResponse<any>> {
    const publicTest = await this.makeRequest('/status', 'GET');
    if (!publicTest.success) {
      return {
        success: false,
        error: 'Falha na conectividade básica com a UazAPI',
        message: publicTest.error
      };
    }
    
    const authTest = await this.makeRequest('/instance/status', 'GET');
    if (!authTest.success) {
      return {
        success: false,
        error: 'Token inválido ou problema de autenticação',
        message: authTest.error
      };
    }

    return {
      success: true,
      data: authTest.data,
      message: 'Conectividade e autenticação OK'
    };
  }

  /**
   * Listar todas as instâncias (requer admintoken)
   * @author Matheus
   * @returns Promise com array de instâncias
   */
  async listAllInstances(): Promise<UazApiResponse<WhatsAppInstance[]>> {
    return this.makeRequest<WhatsAppInstance[]>('/instance/all', 'GET', undefined, true);
  }

  /**
   * Obtém status da instância WhatsApp
   * @author Matheus
   * @param instanceId ID da instância (opcional)
   * @returns Promise com status da instância
   */
  async getInstanceStatus(instanceId?: string): Promise<UazApiResponse<InstanceStatusResponse>> {
    try {
      const response = await this.makeRequest<any>('/instance/status', 'GET');
      
      if (response.success && response.data) {
        const instanceData = response.data.instance;
        const statusData = response.data.status;
        
        return {
          success: true,
          data: {
            instance: instanceData,
            status: {
              connected: statusData?.connected || false,
              jid: statusData?.jid || '',
              loggedIn: statusData?.loggedIn || false
            }
          }
        };
      }
      
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Erro ao obter status da instância'
      };
    }
  }

  /**
   * Conectar instância WhatsApp para gerar QR Code (sem phone)
   * @author Matheus
   * @returns Promise com dados da instância e QR Code
   */
  async connectInstanceForQR(): Promise<UazApiResponse<WhatsAppInstance>> {
    try {
      const statusResponse = await this.getInstanceStatus();
      
      if (statusResponse.success && statusResponse.data?.instance) {
        const instance = statusResponse.data.instance;
        const isConnected = statusResponse.data.status?.connected || instance.status === 'connected';
        
        if (isConnected) {
          return {
            success: true,
            data: instance,
            message: 'Instância já está conectada'
          };
        }
        
        if (instance.qrcode && instance.qrcode.trim()) {
          return {
            success: true,
            data: instance,
            message: 'QR Code já disponível'
          };
        }
      }
      
      const response = await this.makeRequest<any>('/instance/connect', 'POST');
      
      if (response.success && response.data) {
        const instanceData = response.data.instance || response.data;
        
        return {
          success: true,
          data: instanceData,
          message: response.data.connected ? 'Instância conectada' : 'QR Code gerado, aguardando escaneamento'
        };
      }
      
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Erro ao gerar QR Code'
      };
    }
  }

  /**
   * Conecta instância WhatsApp com phone (para pairing code)
   * @author Matheus
   * @param phone Número de telefone
   * @returns Promise com dados da instância
   */
  async connectInstance(phone: string): Promise<UazApiResponse<WhatsAppInstance>> {
    if (!phone) {
      return {
        success: false,
        error: 'Parâmetro phone é obrigatório para conectar a instância'
      };
    }
    
    const requestBody = { phone };
    
    try {
      const response = await this.makeRequest<any>('/instance/connect', 'POST', requestBody);
      
      if (response.success && response.data) {
        const instanceData = response.data.instance || response.data;
        
        return {
          success: true,
          data: instanceData,
          message: response.data.connected ? 'Instância conectada' : 'Processo de conexão iniciado'
        };
      }
      
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Erro ao conectar instância'
      };
    }
  }

  /**
   * Conectar instância com verificação de status e retry inteligente (recomendado para evitar erro 409)
   * @author Matheus
   * @param phone Número de telefone
   * @param maxRetries Número máximo de tentativas
   * @returns Promise com dados da instância
   */
  async connectInstanceSafe(phone: string, maxRetries: number = 3): Promise<UazApiResponse<WhatsAppInstance>> {
    if (!phone) {
      return {
        success: false,
        error: 'Parâmetro phone é obrigatório para conectar a instância'
      };
    }
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const statusResponse = await this.getInstanceStatus();
        
        if (statusResponse.success && statusResponse.data?.instance) {
          const currentStatus = statusResponse.data.instance.status;
          const isConnected = statusResponse.data.status?.connected || false;

          if (currentStatus === 'connected' || isConnected) {
            return {
              success: true,
              data: statusResponse.data.instance,
              message: 'Instância já está conectada'
            };
          }
          
          if (currentStatus === 'connecting') {
            const waitTime = attempt === 1 ? 10000 : 20000;
            
            await new Promise(resolve => setTimeout(resolve, waitTime));
            
            const retryStatus = await this.getInstanceStatus();
            
            if (retryStatus.success && retryStatus.data?.instance) {
              const retryConnected = retryStatus.data.status?.connected || retryStatus.data.instance.status === 'connected';
              
              if (retryConnected) {
                return {
                  success: true,
                  data: retryStatus.data.instance,
                  message: 'Instância conectada com sucesso'
                };
              }
            }
          }
        }
        
        const connectResponse = await this.connectInstance(phone);
        
        if (connectResponse.success) {
          await new Promise(resolve => setTimeout(resolve, 3000));
          
          const finalStatus = await this.getInstanceStatus();
          
          if (finalStatus.success && finalStatus.data?.instance) {
            return {
              success: true,
              data: finalStatus.data.instance,
              message: connectResponse.message || 'Processo de conexão iniciado'
            };
          }
          
          return connectResponse;
        }
        
        const errorMessage = connectResponse.message || '';
        if (errorMessage.includes('409') || errorMessage.toLowerCase().includes('connection') || errorMessage.toLowerCase().includes('progress')) {
          if (attempt === maxRetries) {
            return {
              success: false,
              error: 'Tentativa de conexão em progresso. Aguarde alguns minutos antes de tentar novamente.',
              message: 'Erro 409: Connection attempt in progress'
            };
          }
          
          const retryDelay = attempt * 15000;
          
          await new Promise(resolve => setTimeout(resolve, retryDelay));
          continue;
        }
        
        return connectResponse;
        
      } catch (error: any) {
        if (attempt === maxRetries) {
          return {
            success: false,
            error: `Erro ao conectar instância após ${maxRetries} tentativas: ${error.message}`
          };
        }
        
        const errorDelay = 5000;
        
        await new Promise(resolve => setTimeout(resolve, errorDelay));
      }
    }
    
    // Fallback (não deveria chegar aqui)
    return {
      success: false,
      error: 'Falha inesperada no processo de conexão'
    };
  }

  /**
   * Criar nova instância WhatsApp
   * @author Matheus
   * @param name Nome da instância
   * @param systemName Nome do sistema (opcional)
   * @param adminField01 Campo administrativo 1 (opcional)
   * @param adminField02 Campo administrativo 2 (opcional)
   * @returns Promise com dados da instância criada
   */
  async createInstance(name: string, systemName?: string, adminField01?: string, adminField02?: string): Promise<UazApiResponse<WhatsAppInstance>> {
    const requestBody: any = { name };
    if (systemName) {
      requestBody.systemName = systemName;
    }
    if (adminField01) {
      requestBody.adminField01 = adminField01;
    }
    if (adminField02) {
      requestBody.adminField02 = adminField02;
    }
    
    try {
      const response = await this.makeRequest<any>('/instance/init', 'POST', requestBody, true);
      
      if (response.success && response.data) {
        const instanceData = response.data.instance || response.data;
        
        return {
          success: true,
          data: instanceData,
          message: 'Instância criada com sucesso'
        };
      }
      
      return {
        success: false,
        error: response.message || 'Erro ao criar instância'
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Erro desconhecido ao criar instância'
      };
    }
  }

  /**
   * Desconecta instância WhatsApp
   * @author Matheus
   * @param instanceName Nome da instância (opcional)
   * @returns Promise com resultado da desconexão
   */
  async disconnectInstance(instanceName?: string): Promise<UazApiResponse<any>> {
    try {
      if (instanceName) {
        
        const statusResponse = await this.getInstanceStatus();
        if (statusResponse.success && statusResponse.data?.instance) {
          const currentInstanceName = statusResponse.data.instance.name;
          
          if (currentInstanceName !== instanceName) {
            const connectResponse = await this.connectInstance(instanceName);
            if (!connectResponse.success) {
              return {
                success: false,
                error: `Erro ao conectar à instância ${instanceName}: ${connectResponse.message}`
              };
            }
            
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        } else {
          const connectResponse = await this.connectInstance(instanceName);
          if (!connectResponse.success) {
            return {
              success: false,
              error: `Erro ao conectar à instância ${instanceName}: ${connectResponse.message}`
            };
          }
          
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
      
      const disconnectResponse = await this.makeRequest('/instance/disconnect', 'POST');
      
      if (disconnectResponse.success) {
        
        if (instanceName) {
          try {
            const supabase = (await import('../supabase/client')).default;
            const { error: updateError } = await supabase
              .from('instances')
              .update({ 
                status: false, // false = disconnected
                updated_at: new Date().toISOString()
              })
              .eq('whatsapp', instanceName);
            
            if (updateError) {
            }
          } catch (supabaseError) {
          }
        }
        
        return {
          success: true,
          data: disconnectResponse.data,
          message: `Instância ${instanceName || 'atual'} desconectada com sucesso. Sessão encerrada completamente.`
        };
      } else {
        return {
          success: false,
          error: disconnectResponse.message || 'Erro ao desconectar instância'
        };
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Erro inesperado ao desconectar instância'
      };
    }
  }

  /**
   * Deletar instância WhatsApp
   * @author Matheus
   * @param instanceId ID da instância (opcional)
   * @returns Promise com resultado da exclusão
   */
  async deleteInstance(instanceId?: string): Promise<UazApiResponse<any>> {
    try {
      const endpoint = instanceId ? `/instance/delete/${instanceId}` : '/instance/delete';
      const response = await this.makeRequest(endpoint, 'DELETE', undefined, true);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Erro desconhecido ao deletar instância'
      };
    }
  }

  /**
   * Deletar instância WhatsApp com token específico
   * @author Matheus
   * @param instanceToken Token da instância
   * @returns Promise com resultado da exclusão
   */
  async deleteInstanceWithToken(instanceToken: string): Promise<UazApiResponse<any>> {
    try {
      await this.getInstanceStatusWithToken(instanceToken);
      
      const response = await this.makeRequest('/instance', 'DELETE', undefined, false, instanceToken);
      
      if (response.success) {
        await this.getInstanceStatusWithToken(instanceToken);
      }
      
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Erro desconhecido ao deletar instância'
      };
    }
  }

  /**
   * Conectar instância WhatsApp com token específico (sempre gera QR code)
   * @author Matheus
   * @param instanceToken Token da instância
   * @returns Promise com dados da conexão
   */
  async connectInstanceWithToken(instanceToken: string): Promise<UazApiResponse<any>> {
    try {
      const requestBody = {};
      
      const response = await this.makeRequest('/instance/connect', 'POST', requestBody, false, instanceToken);
      
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Erro desconhecido ao conectar instância'
      };
    }
  }

  /**
   * Desconecta instância WhatsApp com token específico
   * @author Matheus
   * @param instanceToken Token da instância
   * @returns Promise com resultado da desconexão
   */
  async disconnectInstanceWithToken(instanceToken: string): Promise<UazApiResponse<any>> {
    try {
      const response = await this.makeRequest('/instance/disconnect', 'POST', undefined, false, instanceToken);
      
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Erro desconhecido ao desconectar instância'
      };
    }
  }

  /**
   * Envia mensagem de texto via WhatsApp
   * @author Matheus
   * @param request Dados da mensagem
   * @returns Promise com resultado do envio
   */
  async sendTextMessage(request: SendMessageRequest): Promise<UazApiResponse<any>> {

    
    const result = await this.makeRequest('/send/text', 'POST', {
      number: request.number,
      text: request.text,
      readchat: request.readchat ?? true
    });
    

    return result;
  }

  /**
   * Enviar mídia
   * @author Matheus
   * @param request Dados da mídia
   * @returns Promise com resultado do envio
   */
  async sendMedia(request: SendMediaRequest): Promise<UazApiResponse<any>> {
    try {
      const response = await this.makeRequest<any>('/send/media', 'POST', request);
      
      if (response.success) {
      } else {
      }
      
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Erro desconhecido ao enviar mídia'
      };
    }
  }

  /**
   * Deletar mensagem para todos
   * @author Matheus
   * @param messageId ID da mensagem
   * @returns Promise com resultado da exclusão
   */
  async deleteMessage(messageId: string): Promise<UazApiResponse<any>> {
    try {
      const response = await this.makeRequest<any>('/message/delete', 'POST', {
        id: messageId
      });
      
      if (response.success) {
      } else {
      }
      
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Erro desconhecido ao deletar mensagem'
      };
    }
  }

  /**
   * Obter QR Code para conexão
   * @author Matheus
   * @param phone Número de telefone (opcional)
   * @returns Promise com QR Code
   */
  async getQRCode(phone?: string): Promise<UazApiResponse<{ qrcode: string }>> {
    try {
      const statusResponse = await this.makeRequest<any>('/instance/status', 'GET');
      
      if (statusResponse.success && statusResponse.data?.instance) {
        const currentStatus = statusResponse.data.instance.status;
        const hasQrcode = statusResponse.data.instance.qrcode;
        
        if (hasQrcode) {
          return {
            success: true,
            data: { qrcode: hasQrcode }
          };
        }
        
        if (currentStatus !== 'connecting' && phone) {
          const connectResponse = await this.connectInstance(phone);
          
          if (connectResponse.success) {
            await new Promise(resolve => setTimeout(resolve, 2000));
            
            const newStatusResponse = await this.makeRequest<any>('/instance/status', 'GET');
            
            if (newStatusResponse.success && newStatusResponse.data?.instance?.qrcode) {
              return {
                success: true,
                data: { qrcode: newStatusResponse.data.instance.qrcode }
              };
            }
          }
        }
      }
      
      return {
        success: false,
        error: phone ? 'Erro ao gerar QR Code. Tente novamente.' : 'QR Code não disponível. Número de telefone é necessário para iniciar a conexão.'
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Erro ao obter QR Code'
      };
    }
  }

  /**
   * Obter código de pareamento
   * @author Matheus
   * @returns Promise com código de pareamento
   */
  async getPairCode(): Promise<UazApiResponse<{ paircode: string }>> {
    try {
      const response = await this.makeRequest<any>('/instance/status', 'GET');
      
      if (response.success && response.data?.instance?.paircode) {
        return {
          success: true,
          data: { paircode: response.data.instance.paircode }
        };
      }
      
      return {
        success: false,
        error: 'Código de pareamento não disponível. Inicie a conexão primeiro.'
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Erro ao obter código de pareamento'
      };
    }
  }

  /**
   * Validar token
   * @author Matheus
   * @param token Token a ser validado
   * @returns Promise com resultado da validação
   */
  async validateToken(token: string): Promise<UazApiResponse<any>> {
    const tempConfig = { ...this.config, token };
    const tempService = new UazApiService(tempConfig);
    
    return tempService.makeRequest('/instance/status', 'GET');
  }

  /**
   * Atualizar token
   * @author Matheus
   * @param newToken Novo token
   */
  updateToken(newToken: string): void {
    this.config.token = newToken;
  }

  /**
   * Atualizar admintoken
   * @author Matheus
   * @param newAdminToken Novo token administrativo
   */
  updateAdminToken(newAdminToken: string): void {
    this.config.adminToken = newAdminToken;
  }

  /**
   * Obter configuração atual
   * @author Matheus
   * @returns Configuração atual da API
   */
  getConfig(): UazApiConfig {
    return { ...this.config };
  }

  /**
   * Envia mensagem de texto com token específico
   * @author Matheus
   * @param request Dados da mensagem
   * @param instanceToken Token da instância
   * @returns Promise com resultado do envio
   */
  async sendTextMessageWithToken(request: SendMessageRequest, instanceToken: string): Promise<UazApiResponse<any>> {
    return this.makeRequest('/send/text', 'POST', {
      number: request.number,
      text: request.text,
      readchat: request.readchat ?? true
    }, false, instanceToken);
  }

  /**
   * Envia mídia com token específico
   * @author Matheus
   * @param request Dados da mídia
   * @param instanceToken Token da instância
   * @returns Promise com resultado do envio
   */
  async sendMediaWithToken(request: SendMediaRequest, instanceToken: string): Promise<UazApiResponse<any>> {
    try {
      const response = await this.makeRequest<any>('/send/media', 'POST', request, false, instanceToken);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Erro desconhecido ao enviar mídia'
      };
    }
  }

  /**
   * Obtém status de instância com token específico
   * @author Matheus
   * @param instanceToken Token da instância
   * @returns Promise com status da instância
   */
  async getInstanceStatusWithToken(instanceToken: string): Promise<UazApiResponse<InstanceStatusResponse>> {
    try {
      const response = await this.makeRequest<any>('/instance/status', 'GET', undefined, false, instanceToken);
      
      if (response.success && response.data) {
        const instanceData = response.data.instance;
        const statusData = response.data.status;
        
        return {
          success: true,
          data: {
            instance: instanceData,
            status: {
              connected: statusData?.connected || false,
              jid: statusData?.jid || '',
              loggedIn: statusData?.loggedIn || false
            }
          },
          message: 'Status obtido com sucesso'
        };
      }
      
      return {
        success: false,
        error: 'Não foi possível obter o status da instância'
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Erro ao obter status da instância'
      };
    }
  }

  /**
   * Busca o token da instância ativa no banco de dados
   * @author Matheus
   * @param clinicId ID da clínica
   * @returns Promise com token da instância ativa
   */
  async getActiveInstanceToken(clinicId: number): Promise<UazApiResponse<{ token: string; instanceId: number }>> {
    try {

      
      const response = await apiService.instances.get(`clinic_id=${clinicId}&status=true&select=id,token,name,whatsapp&order=created_at&desc=true&limit=1`);

      if (!response.sucesso) {
        return {
          success: false,
          error: `Erro ao buscar instância ativa: ${response.mensagem}`
        };
      }

      const data = response.dados;

      if (!data || data.length === 0) {
        return {
          success: false,
          error: 'Nenhuma instância WhatsApp ativa encontrada para esta clínica'
        };
      }

      const instance = data[0]; // Pega a primeira instância (mais recente)

      if (!instance.token) {
        return {
          success: false,
          error: 'Instância encontrada mas não possui token válido'
        };
      }



      return {
        success: true,
        data: {
          token: instance.token,
          instanceId: instance.id
        },
        message: 'Token da instância obtido com sucesso'
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Erro crítico ao buscar token da instância'
      };
    }
  }


}

const uazApiService = new UazApiService({
  baseUrl: process.env.REACT_APP_UAZAPI_BASE_URL || 'https://smileia.uazapi.com',
  token: process.env.REACT_APP_UAZAPI_INSTANCE_TOKEN || '',
  adminToken: process.env.REACT_APP_UAZAPI_ADMIN_TOKEN || ''
});

export default uazApiService;
